import { GetServerSideProps, GetServerSidePropsContext } from 'next'

import Main from '@/layouts/main'
import Snackbar from '@/components/snackbar'
import CTARayCard from '@/components/cards/cta/cta-ray'
import { Banner } from '@/sections/repeatable/vacancy-overview/banner'
import VacanciesSection from '@/sections/repeatable/vacancy-overview/vacancies'
import { getSessionID } from '@/services/firebase/admin/utils/session'
import PageProps from '@/interfaces/page'
import {
  getRvdbNavigationData,
  getVacanciesOverviewPageData,
} from '@/services/contentful/api'
import { useSmallScreen } from '@/hooks/useMediaQuery'
import useVacanciesData, {
  getServerSideVacanciesData,
} from '@/hooks/useVacanciesData'
import { VacancyPage } from '@/interfaces/vacancy'

const VacanciesPage = ({ data: pageData, user }: PageProps<VacancyPage>) => {
  const {
    bannerImage,
    bannerIcon,
    bannerTitle,
    bannerSubtitle,
    rayCta,
    filtersData,
    serverSideVacancies,
  } = pageData
  const {
    vacancies,
    filters,
    searchQuery,
    totalPages,
    currentPage,
    errorMessage,
    isErrorSnackbarOpen,
    isLoading,
    displayTextkernel,
    handleCloseErrorSnackbar,
    handleDisplayTextkernel,
    handleCheckBoxChange,
    handleSearchChange,
    handlePageChange,
  } = useVacanciesData({ serverSideVacancies })

  const isSmallScreen = useSmallScreen()

  return (
    <Main withGradient={!isSmallScreen}>
      <Banner
        handleInputSearchQueryChange={handleSearchChange}
        searchQuery={searchQuery}
        loading={isLoading}
        displayTextkernel={displayTextkernel}
        handleDisplayTextkernel={handleDisplayTextkernel}
        bannerImage={bannerImage}
        bannerIcon={bannerIcon}
        bannerTitle={bannerTitle}
        bannerSubtitle={bannerSubtitle}
      />
      <VacanciesSection
        vacancies={vacancies}
        currentPage={currentPage}
        totalPages={totalPages}
        matchCriteriaData={filtersData}
        filters={filters}
        loadingVacancies={isLoading}
        handlePageChange={handlePageChange}
        handleCheckBoxChange={handleCheckBoxChange}
        ctaCard={
          <CTARayCard
            className="col-span-full order-5"
            title={rayCta?.title ?? ''}
            benefitList={rayCta?.benefitList ?? []}
            image={rayCta?.image}
            buttonText={rayCta?.buttonText ?? ''}
            buttonLink={rayCta?.buttonPath ?? ''}
          />
        }
      />
      <Snackbar
        message={errorMessage}
        isOpen={isErrorSnackbarOpen}
        onClose={handleCloseErrorSnackbar}
      />
    </Main>
  )
}

export const getServerSideProps: GetServerSideProps = async ({
  preview,
  query,
}: GetServerSidePropsContext) => {
  const isPreview = !!preview ?? false
  const { page, limit, f, q } = query

  const pageNumber = Number(page) || 1
  const limitNumber = Number(limit) || 10
  const searchQuery = (q as string) ?? ''
  const filters = typeof f === 'string' ? [f] : Array.isArray(f) ? f : []

  const sid = await getSessionID()

  const { filtersData, vacanciesData } = await getServerSideVacanciesData({
    sid,
    pageNumber,
    limitNumber,
    filters,
    searchQuery,
  })

  const { data: pageData, error: pageError } =
    await getVacanciesOverviewPageData(isPreview)
  const { data: navData, error: navError } = await getRvdbNavigationData(
    isPreview
  )

  return {
    props: {
      data: {
        navData,
        pageData: {
          ...pageData,
          filtersData,
          serverSideVacancies: vacanciesData,
        },
      },
      error: { pageError, navError },
    },
  }
}

export default VacanciesPage
